import { useMemo } from 'react';
import { GeneralPricingMode, Taxation } from '@mews-commander/data-access';
import { divideNumbersAsDecimals, multiplyNumbersAsDecimals, sumNumbersAsDecimals } from '@mews/framework';
import { TaxRateStrategyDiscriminator, TaxValue } from '@mews-commander/types';
import { getCurrentEnterprise } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getCurrentEnterpriseTaxations } from '@mews-commander/legacy-entities/src/selectors/taxEnvironments';
import { useCommanderSelector } from '@mews-commander/utils';

interface UseEditItemTotalAmountProps {
    taxValues: TaxValue[];
    price: number;
}

// as this is an only use-case for this number
// we are calculating it here
// but we have an investigation ticket in place -- https://mews.atlassian.net/browse/OPS-8710
// as it would be preferable to have one sorce of truth
// for tax values/numbers
const getTaxFromGross = (taxRate: number) => divideNumbersAsDecimals(taxRate, sumNumbersAsDecimals(1, taxRate));

export const useCalculateEditAmount = ({ taxValues, price }: UseEditItemTotalAmountProps) => {
    const hasNetPricing = useCommanderSelector(
        (state) => getCurrentEnterprise(state).pricing === GeneralPricingMode.Net
    );

    const itemTaxCodes = taxValues.map(({ taxRateCode }) => taxRateCode);

    const taxations: Taxation[] = useCommanderSelector((state) => getCurrentEnterpriseTaxations(state, {}));

    const filteredTaxRates = useMemo(
        () =>
            taxations
                .flatMap(({ taxRates }) => taxRates)
                .filter(
                    (taxRate) =>
                        taxRate &&
                        taxRate.strategy?.discriminator === TaxRateStrategyDiscriminator.Relative &&
                        itemTaxCodes.includes(taxRate.code || '')
                )
                .map((taxRate) => {
                    if (!hasNetPricing) return getTaxFromGross(taxRate?.strategy?.value?.value || 0);

                    return taxRate?.strategy?.value?.value || 0;
                }),
        [taxations, hasNetPricing, itemTaxCodes]
    );

    if (!filteredTaxRates.length || !taxValues.length) {
        return {
            value: price || 0,
            totalTaxValue: 0,
        };
    }

    const totalTaxValue = filteredTaxRates
        .map((taxRate) => multiplyNumbersAsDecimals(taxRate, price))
        .reduce((taxRate, acc) => sumNumbersAsDecimals(taxRate, acc), 0);

    if (!hasNetPricing) {
        return {
            value: price,
            totalTaxValue,
        };
    }

    const taxAddedPrice = sumNumbersAsDecimals(price, totalTaxValue);

    return {
        value: taxAddedPrice,
        totalTaxValue,
    };
};
